import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Select, DatePicker, message, Input, AutoComplete, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import * as invoiceApi from '../../services/invoiceApi';
import dayjs from 'dayjs';
import DateRangeFilter from '../../components/DateRangeFilter';
import { trimFormValues } from '../../utils/trimFormValues';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TabPane } = Tabs;

const InvoiceCreate = () => {
  const [form] = Form.useForm();
  const [manualForm] = Form.useForm();
  const [jobs, setJobs] = useState([]);
  const [filteredManualJobs, setFilteredManualJobs] = useState([]); // Store filtered jobs for manual selection
  const [channels, setChannels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const navigate = useNavigate();
  const [showExtraCharges, setShowExtraCharges] = useState(false);
  const [showManualExtraCharges, setShowManualExtraCharges] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [manualSendEmail, setManualSendEmail] = useState(false);
  const [dueOnly, setDueOnly] = useState(false);
  const [selectAllJobs, setSelectAllJobs] = useState(false);

  useEffect(() => {
    // Fetch channels and unique customers for dropdowns
    const fetchData = async () => {
      try {
        const [channelsRes, customersRes] = await Promise.all([
          apiService.channels.getAll(),
          apiService.customers.getUniqueNames ? apiService.customers.getUniqueNames() : apiService.customers.getUnique(),
        ]);
        setChannels(channelsRes.data || []);
        setCustomers((customersRes.data || []).map(c => c.name));
      } catch (err) {
        message.error('Failed to load channels or customers');
      }
    };
    fetchData();
    // Set default form values
    form.setFieldsValue({
      invoiceDate: dayjs(),
      dateFilter: {
        year: dayjs().year(),
        month: dayjs().month(),
      }
    });
    manualForm.setFieldsValue({
      invoiceDate: dayjs(),
    });
  }, [form, manualForm]);

  // Refetch jobs when dueOnly changes
  useEffect(() => {
    const channelName = form.getFieldValue('channel');
    const dateFilter = form.getFieldValue('dateFilter');
    if (channelName) {
      fetchAutomaticJobs(channelName, dateFilter);
    }
  }, [dueOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch jobs from server with filters for automatic tab (same as JobList)
  const fetchAutomaticJobs = async (channelName, dateFilter) => {
    if (!channelName) {
      setJobs([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        limit: 1000,
        channel: channelName,
        plPosition: 'income',
        paymentStatus: dueOnly ? 'overdue' : 'pending,overdue'
      };

      // Add date filters
      if (dateFilter) {
        if (dateFilter.dateRange && dateFilter.dateRange.length === 2) {
          // Custom date range selected
          params.startDate = dateFilter.dateRange[0].format('YYYY-MM-DD');
          params.endDate = dateFilter.dateRange[1].format('YYYY-MM-DD');
        } else if (dateFilter.year !== undefined && dateFilter.month !== undefined && dateFilter.month !== 'custom') {
          // Specific month selected
          params.year = dateFilter.year;
          params.month = dateFilter.month;
        }
      }

      const response = await apiService.jobs.getAll(params);
      const jobsData = response.data.data || [];
      setJobs(jobsData);
      
      // Clear previously selected jobs when filters change
      form.setFieldsValue({ jobIds: [] });
    } catch (err) {
      message.error('Failed to load jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs by selected channel and date range
  const handleChannelChange = (channelName) => {
    const dateFilter = form.getFieldValue('dateFilter');
    fetchAutomaticJobs(channelName, dateFilter);
  };

  // Filter jobs by date range
  const handleDateFilterChange = (dateFilter) => {
    const channelName = form.getFieldValue('channel');
    fetchAutomaticJobs(channelName, dateFilter);
  };

  const onFinish = async (values) => {
    const trimmedValues = trimFormValues(values);
    if (!trimmedValues.jobIds?.length && !trimmedValues.channel && !trimmedValues.customer) {
      message.error('Please select at least one criterion: jobs, channel, or customer.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        invoiceDate: trimmedValues.invoiceDate.format('YYYY-MM-DD'),
      };
      if (trimmedValues.orderNo) {
        payload.orderNo = trimmedValues.orderNo;
      }
      if (trimmedValues.dueDate) {
        payload.dueDate = trimmedValues.dueDate.format('YYYY-MM-DD');
      }
      if (trimmedValues.channel) {
        payload.channel = trimmedValues.channel;
      }
      if (trimmedValues.jobIds?.length) {
        payload.jobIds = trimmedValues.jobIds;
      } else {
        if (trimmedValues.customer) payload.customer = trimmedValues.customer;
        if (trimmedValues.dateFilter) {
          if (trimmedValues.dateFilter.dateRange) {
            payload.startDate = trimmedValues.dateFilter.dateRange[0].format('YYYY-MM-DD');
            payload.endDate = trimmedValues.dateFilter.dateRange[1].format('YYYY-MM-DD');
          } else {
            const startDate = dayjs().year(trimmedValues.dateFilter.year).month(trimmedValues.dateFilter.month).startOf('month');
            const endDate = dayjs().year(trimmedValues.dateFilter.year).month(trimmedValues.dateFilter.month).endOf('month');
            payload.startDate = startDate.format('YYYY-MM-DD');
            payload.endDate = endDate.format('YYYY-MM-DD');
          }
        } else {
          payload.startDate = dayjs().format('YYYY-MM-DD');
          payload.endDate = dayjs().format('YYYY-MM-DD');
        }
      }
      if (dueOnly) {
        payload.dueOnly = true;
      }
      if (showExtraCharges && trimmedValues.extraCharges && trimmedValues.extraCharges.length > 0) {
        payload.extraCharges = trimmedValues.extraCharges;
      }
      if (sendEmail) {
        payload.sendEmail = true;
      }
      await invoiceApi.createInvoice(payload);
      message.success('Invoice created!');
      navigate('/invoices');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const onManualFinish = async (values) => {
    const trimmedValues = trimFormValues(values);
    if (!trimmedValues.jobIds?.length) {
      message.error('Please select at least one job for the invoice.');
      return;
    }
    setManualLoading(true);
    try {
      const payload = {
        invoiceDate: trimmedValues.invoiceDate.format('YYYY-MM-DD'),
        jobIds: trimmedValues.jobIds,
        isManual: true, // Flag to indicate this is a manual invoice creation
      };
      if (trimmedValues.orderNo) {
        payload.orderNo = trimmedValues.orderNo;
      }
      if (trimmedValues.dueDate) {
        payload.dueDate = trimmedValues.dueDate.format('YYYY-MM-DD');
      }
      if (showManualExtraCharges && trimmedValues.extraCharges && trimmedValues.extraCharges.length > 0) {
        payload.extraCharges = trimmedValues.extraCharges;
      }
      if (manualSendEmail) {
        payload.sendEmail = true;
      }
      await invoiceApi.createInvoice(payload);
      message.success('Manual invoice created!');
      navigate('/invoices');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to create manual invoice');
    } finally {
      setManualLoading(false);
    }
  };

  // Fetch jobs from server with filters (same as JobList)
  const fetchManualJobs = async (channelName, customerName, dateFilter) => {
    if (!channelName && !customerName) {
      setFilteredManualJobs([]);
      return;
    }

    setManualLoading(true);
    try {
      const params = {
        limit: 1000,
        channel: channelName,
        customer: customerName,
        plPosition: 'income', // Only fetch income jobs
      };

      // Add date filters
      if (dateFilter) {
        if (dateFilter.dateRange && dateFilter.dateRange.length === 2) {
          // Custom date range selected
          params.startDate = dateFilter.dateRange[0].format('YYYY-MM-DD');
          params.endDate = dateFilter.dateRange[1].format('YYYY-MM-DD');
        } else if (dateFilter.year !== undefined && dateFilter.month !== undefined && dateFilter.month !== 'custom') {
          // Specific month selected
          params.year = dateFilter.year;
          params.month = dateFilter.month;
        }
      }

      const response = await apiService.jobs.getAll(params);
      const jobsData = response.data.data || [];
      setFilteredManualJobs(jobsData);
      
      // Clear previously selected jobs when filters change
      manualForm.setFieldsValue({ jobIds: [] });
      setSelectAllJobs(false);
    } catch (err) {
      message.error('Failed to load jobs');
      setFilteredManualJobs([]);
    } finally {
      setManualLoading(false);
    }
  };

  // Filter manual jobs by selected channel
  const handleManualChannelChange = (channelName) => {
    const customerName = manualForm.getFieldValue('customer');
    const dateFilter = manualForm.getFieldValue('dateRange');
    fetchManualJobs(channelName, customerName, dateFilter);
  };

  // Filter manual jobs by selected customer
  const handleManualCustomerChange = (customerName) => {
    const channelName = manualForm.getFieldValue('channel');
    const dateFilter = manualForm.getFieldValue('dateRange');
    fetchManualJobs(channelName, customerName, dateFilter);
  };

  // Filter manual jobs by date range
  const handleManualDateRangeChange = (dateFilter) => {
    const channelName = manualForm.getFieldValue('channel');
    const customerName = manualForm.getFieldValue('customer');
    fetchManualJobs(channelName, customerName, dateFilter);
  };



  // Handle select all jobs
  const handleSelectAllJobs = (checked) => {
    setSelectAllJobs(checked);
    if (checked) {
      // Select all filtered jobs
      const allJobIds = filteredManualJobs.map(job => job._id);
      manualForm.setFieldsValue({ jobIds: allJobIds });
    } else {
      // Deselect all jobs
      manualForm.setFieldsValue({ jobIds: [] });
    }
  };

  // Handle individual job selection
  const handleJobSelection = (selectedJobIds) => {
    const allJobIds = filteredManualJobs.map(job => job._id);
    const allSelected = allJobIds.length > 0 && allJobIds.every(id => selectedJobIds.includes(id));
    setSelectAllJobs(allSelected);
  };

  return (
    <Card title="Create Invoice" style={{ maxWidth: 800, margin: 'auto', marginTop: 32 }}>
      <Tabs defaultActiveKey="automatic">
        <TabPane tab="Automatic Invoice" key="automatic">
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              rules={[{ required: true, message: 'Invoice date is required' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="orderNo" label="Order No (optional)">
              <Input placeholder="Enter order number" />
            </Form.Item>
            <Form.Item
              name="dueDate"
              label="Due Date (optional)"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="channel" label="Channel (optional)">
              <Select 
                allowClear 
                placeholder="Select channel"
                onChange={handleChannelChange}
              >
                {channels.map(channel => (
                  <Option key={channel._id || channel.name} value={channel.name}>
                    {channel.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="jobIds" label="Jobs (optional)">
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select one or more jobs"
                optionFilterProp="children"
                filterOption={(input, option) => {
                  if (!option || !option.children) return false;
                  const childrenText = typeof option.children === 'string' 
                    ? option.children 
                    : String(option.children);
                  return childrenText.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {jobs.map(job => (
                  <Option key={job._id} value={job._id}>
                    {job.bookingReference || job.reference} - {job.customer?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="customer" label="Customer (optional)">
              <AutoComplete
                options={customers.map(name => ({ value: name }))}
                placeholder="Enter or select customer name"
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
                allowClear
              />
            </Form.Item>
            <Form.Item name="dateFilter" label="Date Range (optional)">
              <DateRangeFilter onChange={handleDateFilterChange} />
            </Form.Item>
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  type="dashed"
                  onClick={() => {
                    setShowExtraCharges((prev) => {
                      const next = !prev;
                      if (next && !form.getFieldValue('extraCharges')) {
                        form.setFieldsValue({ extraCharges: [{}] });
                      }
                      return next;
                    });
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  {showExtraCharges ? 'Remove Extra Charges' : 'Add Extra Charges'}
                </Button>
              )}
            </Form.Item>
            {showExtraCharges && (
              <Form.List name="extraCharges">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          rules={[{ required: true, message: 'Charge name required' }]}
                          style={{ flex: 2 }}
                        >
                          <Input placeholder="Charge Name" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'amount']}
                          rules={[{ required: true, message: 'Amount required' }]}
                          style={{ flex: 1 }}
                        >
                          <Input type="number" min={0} step={0.01} placeholder="Amount" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'vat']}
                          rules={[{ required: true, message: 'VAT required' }]}
                          style={{ flex: 1 }}
                        >
                          <Input type="number" min={0} max={100} step={1} placeholder="VAT %" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', marginTop: 8 }} />
                      </div>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Another Charge
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            )}
            <Form.Item>
              <label>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={e => setSendEmail(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send invoice as email to customer
              </label>
            </Form.Item>
            <Form.Item>
              <label>
                <input
                  type="checkbox"
                  checked={dueOnly}
                  onChange={e => setDueOnly(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Only show due jobs
              </label>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Invoice
              </Button>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane tab="Manual Invoice" key="manual">
          <Form form={manualForm} layout="vertical" onFinish={onManualFinish}>
            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              rules={[{ required: true, message: 'Invoice date is required' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="orderNo" label="Order No (optional)">
              <Input placeholder="Enter order number" />
            </Form.Item>
            <Form.Item
              name="dueDate"
              label="Due Date (optional)"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="channel" label="Channel (optional)">
              <Select 
                allowClear 
                placeholder="Select channel to filter jobs"
                onChange={handleManualChannelChange}
              >
                {channels.map(channel => (
                  <Option key={channel._id || channel.name} value={channel.name}>
                    {channel.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="customer" label="Customer (optional)">
              <AutoComplete
                options={customers.map(name => ({ value: name }))}
                placeholder="Enter or select customer name"
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
                allowClear
                onChange={handleManualCustomerChange}
              />
            </Form.Item>

            <Form.Item name="dateRange" label="Date Range (optional)">
              <DateRangeFilter onChange={handleManualDateRangeChange} />
            </Form.Item>
            <div style={{ marginBottom: 8 }}>
              <label>
                <input
                  type="checkbox"
                  checked={selectAllJobs}
                  onChange={e => handleSelectAllJobs(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Select All ({filteredManualJobs.length} jobs available)
              </label>
            </div>
            <Form.Item 
              name="jobIds" 
              label="Select Jobs (required)"
              rules={[{ required: true, message: 'Please select at least one job' }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Select one or more jobs (includes all jobs - paid and unpaid)"
                optionFilterProp="children"
                filterOption={(input, option) => {
                  if (!option || !option.children) return false;
                  const childrenText = typeof option.children === 'string' 
                    ? option.children 
                    : String(option.children);
                  return childrenText.toLowerCase().includes(input.toLowerCase());
                }}
                onChange={handleJobSelection}
              >
                {filteredManualJobs.map(job => (
                  <Option key={job._id} value={job._id}>
                {job.bookingReference || job.reference} - {job.customer?.name} 
                {job.payment?.status === 'paid' ? ' (PAID)' : ''}
              </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  type="dashed"
                  onClick={() => {
                    setShowManualExtraCharges((prev) => {
                      const next = !prev;
                      if (next && !manualForm.getFieldValue('extraCharges')) {
                        manualForm.setFieldsValue({ extraCharges: [{}] });
                      }
                      return next;
                    });
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  {showManualExtraCharges ? 'Remove Extra Charges' : 'Add Extra Charges'}
                </Button>
              )}
            </Form.Item>
            {showManualExtraCharges && (
              <Form.List name="extraCharges">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          rules={[{ required: true, message: 'Charge name required' }]}
                          style={{ flex: 2 }}
                        >
                          <Input placeholder="Charge Name" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'amount']}
                          rules={[{ required: true, message: 'Amount required' }]}
                          style={{ flex: 1 }}
                        >
                          <Input type="number" min={0} step={0.01} placeholder="Amount" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'vat']}
                          rules={[{ required: true, message: 'VAT required' }]}
                          style={{ flex: 1 }}
                        >
                          <Input type="number" min={0} max={100} step={1} placeholder="VAT %" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', marginTop: 8 }} />
                      </div>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Another Charge
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            )}
            <Form.Item>
              <label>
                <input
                  type="checkbox"
                  checked={manualSendEmail}
                  onChange={e => setManualSendEmail(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send invoice as email to customer
              </label>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={manualLoading}>
                Create Manual Invoice
              </Button>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default InvoiceCreate; 